<template>
  <div>
    <div class="mb-2">
      <b-button
        :disabled="isBanPlayersButtonDisabled"
        :variant="banPlayersButtonVariant"
        @click="banPlayers"
        class="mb-1"
      >
        Заблокировать
      </b-button>

      <b-button
        :disabled="isBlockPlayersEventAccessButtonDisabled"
        :variant="blockPlayersEventAccessButtonVariant"
        @click="blockPlayersEventAccessWithConfirmation"
        class="mb-1"
      >
        Заблокировать доступ к событиям
      </b-button>
    </div>

    <h2>Подозреваемые игроки</h2>

    <table class="filter mb-4">
      <tr>
        <th class="pb-2">
          <label for="window-duration-selector">Последний вход</label>
        </th>
        <td class="pb-2">
          <b-form-select
            id="window-duration-selector"
            v-model="lastLoginWindowDuration"
            :options="lastLoginWindowDurationOptions"
          />
        </td>
      </tr>
      <tr>
        <th>
          <label for="min-money">Монеты</label>
        </th>
        <td>
          <input v-model="minMoneyStr" id="min-money" class="form-control" type="number" min="0">
        </td>
      </tr>
      <tr>
        <th>
          <label for="min-star-amount">Звезды</label>
        </th>
        <td>
          <input v-model="minStarAmountStr" id="min-star-amount" class="form-control" type="number" min="0">
        </td>
      </tr>
    </table>

    <loading-indicator v-if="isLoading"/>

    <p v-if="message !== null">
      {{message}}
    </p>

    <template v-if="isPlayersVisible">
      <p class="mb-2">
        Количество подозреваемых игроков: {{suspectedPlayerAmount | getFormattedNumber}}
      </p>

      <suspected-players-action-table
        :suspectedPlayerContainers="suspectedPlayerContainers"
        :mayBanPlayer="mayBanPlayer"
        v-on:players-selection-requested="selectPlayers"
        v-on:players-deselection-requested="deselectPlayers"
        @player-ban-requested="banPlayer"
      />

      <b-pagination-nav
        v-if="pageAmount > 1"
        :number-of-pages="pageAmount"
        :link-gen="linkGen"
        first-number
        last-number
        use-router
        class="mb-4"
      />
    </template>

  </div>
</template>

<script>
import notifier from '../notifier.js';
import { HTTP } from '../http-common.js';
import auth from '../auth.js';

import { mayBanPlayer         } from '../util.js';
import { convertMomentToStamp } from '../util.js';

/**
 * @typedef { import('../service/confirm.js').ConfirmHandle } ConfirmHandle
 */
import { confirmAction } from '../service/confirm.js';

import { isEventAccessCanBeBlocked    } from '../model/event_access.js';
import { EVENT_ACCESS_STATUS__BLOCKED } from '../model/event_access.js';

import { TIME_UNIT_DAY   } from '../model/event_time_filter.js';
import { TIME_UNIT_MONTH } from '../model/event_time_filter.js';
import { getBegin        } from '../model/event_time_filter.js';

import LoadingIndicator from './LoadingIndicator.vue';
import SuspectedPlayersActionTable from './SuspectedPlayersActionTable.vue';

const PAGE_SIZE = 100;

const LOADING_STATUS__INIT    = 1;
const LOADING_STATUS__LOADING = 2;
const LOADING_STATUS__NORMAL  = 3;
const LOADING_STATUS__ERROR   = 4;

export default {
  name: 'SuspectedPlayers',

  components: {
    LoadingIndicator,
    SuspectedPlayersActionTable,
  },

  data() {
    const DURATION_1_MONTH = {timeUnit: TIME_UNIT_MONTH, amount: 1};

    return {
      actor: auth.getViewer(),

      //-----------------------------------------------------------------------
      // filter
      //-----------------------------------------------------------------------

      lastLoginWindowDuration: DURATION_1_MONTH,
      lastLoginWindowDurationOptions: createDurationOptions(),

      minMoneyStr: '',
      minStarAmountStr: '',

      //-----------------------------------------------------------------------

      loadingStatus: LOADING_STATUS__INIT,

      /** @type {?number} */
      suspectedPlayerAmount: null,

      /** @type {Object[] | null} */
      suspectedPlayerContainers: null,
    };
  },

  props: {
    page: {
      type: Number,
      default: 1,
    },
  },

  computed: {
    /** @returns {?string} */
    minMoney() {
      const minMoneyStr = this.minMoneyStr;
      if(minMoneyStr.length === 0) {
        return null;
      }

      return parseInt(minMoneyStr, 10);
    },

    /** @returns {?string} */
    minStarAmount() {
      const minStarAmountStr = this.minStarAmountStr;
      if(minStarAmountStr.length === 0) {
        return null;
      }

      return parseInt(minStarAmountStr, 10);
    },

    /** @returns {boolean} */
    isLoading() {
      return this.loadingStatus === LOADING_STATUS__LOADING;
    },

    /** @returns {boolean} */
    isLoadedNormal() {
      return this.loadingStatus === LOADING_STATUS__NORMAL;
    },

    /** @returns {?string} */
    message() {
      if(!this.isLoadedNormal) {
        return null;
      }

      const containers = this.suspectedPlayerContainers;
      if(containers === null) {
        return null;
      }

      if(containers.length === 0) {
        return 'Нет подозреваемых игроков.';
      }

      return null;
    },

    /** @returns {boolean} */
    isPlayersVisible() {
      if(!this.isLoadedNormal) {
        return false;
      }

      const containers = this.suspectedPlayerContainers;
      if(containers === null) {
        return false;
      }

      return containers.length > 0;
    },

    /** @returns {number} */
    pageAmount() {
      const playerAmount = this.suspectedPlayerAmount;
      if(playerAmount === null) {
        return 0;
      }

      return Math.ceil(playerAmount / PAGE_SIZE);
    },

    /** @returns {number} */
    offset() {
      const prevPageAmount = this.page - 1;
      return prevPageAmount * PAGE_SIZE;
    },

    mayBanPlayer() {
      return mayBanPlayer(this.actor.role);
    },

    //-------------------------------------------------------------------------
    // Ban
    //-------------------------------------------------------------------------

    /** @returns {boolean} */
    isBanPlayersButtonDisabled() {
      return !this.hasSelectedPlayers;
    },

    /** @returns {string} */
    banPlayersButtonVariant() {
      if(this.hasSelectedPlayers) {
        return 'danger';
      } else {
        return 'light';
      }
    },

    //-------------------------------------------------------------------------
    // Block event access
    //-------------------------------------------------------------------------

    /** @returns {boolean} */
    isBlockPlayersEventAccessButtonDisabled() {
      return !this.isBlockPlayersEventAccessButtonEnabled;
    },

    /** @returns {boolean} */
    isBlockPlayersEventAccessButtonEnabled() {
      return this.hasSelectedPlayersToBlockEventAccess;
    },

    /** @returns {boolean} */
    hasSelectedPlayersToBlockEventAccess() {
      if(!this.hasSelectedPlayers) {
        return false;
      }

      const containers = this.suspectedPlayerContainers;
      if(containers === null) {
        return false;
      }

      for(const container of containers) {
        if(container.selected && !isEventAccessCanBeBlocked(container.player.eventAccessStatus)) {
          return false;
        }
      }

      return true;
    },

    /** @returns {string} */
    blockPlayersEventAccessButtonVariant() {
      if(this.hasSelectedPlayersToBlockEventAccess) {
        return 'danger';
      } else {
        return 'light';
      }
    },

    //-------------------------------------------------------------------------
    // Selection
    //-------------------------------------------------------------------------

    /** @returns {boolean} */
    hasSelectedPlayers() {
      const containers = this.suspectedPlayerContainers;
      if(containers === null) {
        return false;
      }

      for(const container of containers) {
        if(container.selected) {
          return true;
        }
      }

      return false;
    },

    /** @returns {number[] | null} */
    selectedPlayerIds() {
      const containers = this.suspectedPlayerContainers;
      if(containers === null) {
        return null;
      }

      const ids = [];
      for(const container of containers) {
        if(container.selected) {
          ids.push(container.player.id);
        }
      }

      return ids;
    },
  },

  created() {
    this.loadSuspectedPlayers();
  },

  watch: {
    page() {
      this.loadSuspectedPlayers();
    },

    lastLoginWindowDuration() {
      this.goToFirstPageOrLoad();
    },

    minMoney() {
      this.goToFirstPageOrLoad();
    },

    minStarAmount() {
      this.goToFirstPageOrLoad();
    },
  },

  methods: {
    loadSuspectedPlayers() {
      notifier.deleteSenderNotifications(this);

      this.loadingStatus = LOADING_STATUS__LOADING;

      const url = `/suspected_players`;

      const params = {};

      const lastLoginWindowDuration = this.lastLoginWindowDuration;
      if(lastLoginWindowDuration !== null) {
        const now = convertMomentToStamp(new Date());
        params.begin = getBegin(now, lastLoginWindowDuration);
      }

      const minMoney = this.minMoney;
      if(minMoney !== null) {
        params.money = minMoney;
      }

      const minStarAmount = this.minStarAmount;
      if(minStarAmount !== null) {
        params.star = minStarAmount;
      }

      params.limit = PAGE_SIZE;

      const offset = this.offset;
      if(offset > 0) {
        params.offset = offset;
      }

      HTTP.get(
        url,
        {params: params}
      )
      .then(response => {
        // TODO: use const
        //const data = response.data.data;
        let data = response.data.data;
        //data = getFakeData();

        if(data === null) {
          this.loadingStatus = LOADING_STATUS__ERROR;
          notifier.showError('Invalid server response. data === null.', {sender: this});
          return;
        }

        this.suspectedPlayerAmount = data.playerAmount;

        const players = data.players;
        this.suspectedPlayerContainers = convertSuspectedPlayersToSuspectedPlayerContainers(players);

        this.loadingStatus = LOADING_STATUS__NORMAL;
      })
      .catch(err => {
        this.loadingStatus = LOADING_STATUS__ERROR;
        notifier.showError(err, {sender: this});
      });
    },

    goToFirstPageOrLoad() {
      if(this.page > 1) {
        this.goToFirstPage();
        return;
      }

      this.loadSuspectedPlayers();
    },

    goToFirstPage() {
      const location = {
        name: 'suspected_players',
        params: {page: 1}
      };

      this.$router.push(location);
    },

    linkGen(pageNum) {
      return pageNum === 1 ? '?' : `?page=${pageNum}`;
    },

    //-------------------------------------------------------------------------
    // Selection
    //-------------------------------------------------------------------------

    selectPlayers() {
      for(const container of this.suspectedPlayerContainers) {
        if(!container.selected) {
          container.selected = true;
        }
      }
    },

    deselectPlayers() {
      for(const container of this.suspectedPlayerContainers) {
        if(container.selected) {
          container.selected = false;
        }
      }
    },

    //-------------------------------------------------------------------------
    // Ban (1 player)
    //-------------------------------------------------------------------------

    /** @param {number} playerId */
    banPlayer(playerId) {
      const playerIdsStr = playerId.toString();

      const location = {
        name: 'suspected_players_ban_add',
        query: {player_ids: playerIdsStr}
      };

      this.$router.push(location);
    },

    //-------------------------------------------------------------------------
    // Ban (many players)
    //-------------------------------------------------------------------------

    banPlayers() {
      const playerIdsStr = this.selectedPlayerIds.join(',');

      const location = {
        name: 'suspected_players_ban_add',
        query: {player_ids: playerIdsStr}
      };

      this.$router.push(location);
    },

    //-------------------------------------------------------------------------
    // Block event access (for many players)
    //-------------------------------------------------------------------------

    blockPlayersEventAccessWithConfirmation() {
      notifier.deleteSenderNotifications(this);

      const playerIds = this.selectedPlayerIds;
      
      this.confirmPlayersEventAccessBlock(
        playerIds,
        () => {
          this.blockPlayersEventAccess(playerIds);
        },
      );
    },

    /**
     * @param {number[]} playerIds
     * @param {ConfirmHandle} onConfirmed
     */
    confirmPlayersEventAccessBlock(playerIds, onConfirmed) {
      const messageTemplate = getPlayersBlockConfirmationMessageTemplate(playerIds);

      confirmAction(
        this,
        messageTemplate,
        'Block event access',
        onConfirmed,
      );
    },

    /** @param {number[]} playerIds */
    blockPlayersEventAccess(playerIds) {
      const playerIdsStr = playerIds.join(',');

      const data = new FormData();
      data.set('player_ids', playerIdsStr);

      HTTP.post('/players/block_event_access', data)
      .then(() => {
        this.markEventAccessBlocked(playerIds);
        //deletePlayerContainersFromArray(this.suspectedPlayerContainers, playerIds);
        //this.suspectedPlayerAmount -= playerIds.length;

        const message = 'Доступ к событиям заблокирован.';
        notifier.showSuccess(message);
      })
      .catch(err => {
        notifier.showError(err, {sender: this});
      });
    },

    /** @param {number[]} playerIds */
    markEventAccessBlocked(playerIds) {
      const containers = this.suspectedPlayerContainers;
      if(containers === null) {
        return null;
      }

      for(const container of containers) {
        const player = container.player;
        if(playerIds.includes(player.id)) {
          player.eventAccessStatus = EVENT_ACCESS_STATUS__BLOCKED;
        }
      }
    },
  },
}

// options for component b-form-select
function createDurationOptions() {
  return [
    {
      value: {timeUnit: TIME_UNIT_DAY, amount: 7},
      text: 'последние 7 дней',
    },
    {
      value: {timeUnit: TIME_UNIT_MONTH, amount: 1},
      text: 'последний месяц',
    },
    {
      value: {timeUnit: TIME_UNIT_MONTH, amount: 3},
      text: 'последние 3 месяца',
    },
    {
      value: {timeUnit: TIME_UNIT_MONTH, amount: 6},
      text: 'последние 6 месяцев',
    },
    {
      value: {timeUnit: TIME_UNIT_MONTH, amount: 12},
      text: 'последний год',
    },
    {
      value: null,
      text: 'любое время',
    },
  ];
}

/**
 * @param {Object[]} players
 * @returns {Object[]}
 */
function convertSuspectedPlayersToSuspectedPlayerContainers(players) {
  const containers = [];

  for(const player of players) {
    const container = {
      player: player,
      selected: false,
    };
    containers.push(container);
  }

  return containers;
}


//-------------------------------------------------------------------------
// Block event access (for many players)
//-------------------------------------------------------------------------

/** @param {number[]} playerIds */
function getPlayersBlockConfirmationMessageTemplate(playerIds) {
  if(playerIds.length === 1) {
    const playerId = playerIds[0];
    return getPlayerBlockConfirmationMessageTemplate(playerId);
  }

  const amount = playerIds.length;
  return `Are you sure you want to block event access for ${amount} players {env}?`;
}

/** @param {number} playerId */
function getPlayerBlockConfirmationMessageTemplate(playerId) {
  return `Are you sure you want to block event access for player ${playerId} {env}?`
}

/**
 * @param {Object[]} containers 
 * @param {number[]} playerIds
 */
//function deletePlayerContainersFromArray(containers, playerIds) {
//  for(const playerId of playerIds) {
//    deletePlayerContainerFromArray(containers, playerId);
//  }
//}

/**
 * @param {Object[]} containers 
 * @param {number} playerId
 */
//function deletePlayerContainerFromArray(containers, playerId) {
//  const index = getPlayerContainerIndex(containers, playerId);
//  if(index !== null) {
//    containers.splice(index, 1);
//  }
//}

/**
 * @param {Object[]} containers
 * @param {number} playerId
 * @returns {number | null}
 */
//function getPlayerContainerIndex(containers, playerId) {
//  for(let i = 0; i < containers.length; i++) {
//    const container = containers[i];
//    if(container.player.id === playerId) {
//      return i;
//    }
//  }
//
//  return null;
//}

//-------------------------------------------------------------------------

/*
function getFakeData() {
  return {
    "playerAmount": 5,
    "players": [
      {
        "id": 2032441,
        "name": "Luanna",
        "lastLoginTime": 1697258804,
        "suspicionsMask": 4,
        "money": 455191537,
        "starAmount": 10
      },
      {
        "id": 3382091,
        "name": "Jane",
        "lastLoginTime": 1697463317,
        "suspicionsMask": 2,
        "money": 97711129,
        "starAmount": 0
      },
      {
        "id": 6817269,
        "name": "Nhân",
        "lastLoginTime": 1697429045,
        "suspicionsMask": 17,
        "money": 1000043684,
        "starAmount": 8
      },
      {
        "id": 7449343,
        "name": "Prim",
        "lastLoginTime": 1697440073,
        "suspicionsMask": 2,
        "money": 55547613,
        "starAmount": 13
      },
      {
        "id": 8719543,
        "name": "anyway",
        "lastLoginTime": 1697479726,
        "suspicionsMask": 3,
        "money": 34429277,
        "starAmount": 3727
      }
    ]
  };
}
*/
</script>

<style scoped>
#window-duration-selector {
  min-width: 250px;
}
.filter > tr > th > label {
  margin-bottom: 0;
}
.filter > tr > th {
  padding-right: .2rem;
}
.filter > tr:not(:last-child) >>> th,
.filter > tr:not(:last-child) >>> td {
  padding-bottom: .2rem;
}
</style>
